export interface User {
  id: number;
  name: string;
  email: string;
  email_verified_at?: string;
  nationality?: string;
  document_type?: string;
  document_number?: string;
  date_of_birth?: string;
  phone?: string;
  country_code?: string;
  created_at: string;
  updated_at: string;
}
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  nationality?: string;
  document_type?: string;
  document_number?: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface Companion {
  id: number;
  name: string;
  email: string | null;
  nationality?: string;
  document_type?: string;
  document_number: string;
  date_of_birth: string;
  phone: string | null;
  country_code?: string;
  age?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateCompanionData {
  name: string;
  email?: string | null;
  nationality?: string;
  document_type?: string;
  document_number: string;
  date_of_birth: string;
  phone?: string | null;
  country_code?: string;
}

export interface TripParticipant {
  id: number;
  trip_id: number;
  name: string;
  email: string | null;
  nationality?: string;
  document_type?: string;
  document_number: string;
  date_of_birth: string;
  phone?: string | null;
  country_code?: string;
  exemption_reason_id?: number | null;
  exemption_document_path?: string | null;
  voucher_code?: string;
  individual_amount?: number;
  is_exempt?: boolean;
  qr_code_path?: string | null;
  exemption_reason?: {
    id: number;
    name: string;
    description: string;
    is_active: boolean;
    color: string;
    created_at: string;
    updated_at: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface Trip {
  id: number;
  user_id: number;
  hotel_name: string;
  start_date: string;
  end_date: string;
  total_days: number;
  total_amount: number;
  payment_status_id: number;
  voucher_code?: string;
  voucher_expires_at?: string;
  notes?: string;
  is_owner?: boolean;
  created_at: string;
  updated_at: string;
  participants: TripParticipant[];
  paymentStatus?: {
    id: number;
    name: string;
    description?: string;
    is_active?: boolean;
    color?: string;
  };
  user?: {
    id: number;
    name: string;
    email: string;
  };
  payments?: Payment[];
}

export interface CreateTripData {
  hotel_name: string;
  start_date: string;
  end_date: string;
  participants: {
    name: string;
    email?: string | null;
    document_number: string;
    date_of_birth: string;
    phone?: string | null;
    country_code?: string;
    exemption_reason_id?: number | null;
  }[];
}

export interface Payment {
  id: number;
  trip_id: number;
  amount: number;
  payment_method: string;
  gateway_transaction_id?: string;
  status: string;
  processed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePaymentData {
  trip_id: number;
  amount: number;
  payment_method: string;
  reference_number?: string;
}

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export type PaymentStatus = 'pending' | 'paid' | 'confirmed';

// Helper functions
export const getPaymentStatusName = (paymentStatusId: number): string => {
  switch (paymentStatusId) {
    case 1: return 'pending';
    case 2: return 'paid';
    case 3: return 'failed';
    case 4: return 'paid_offline';
    case 5: return 'cancelled';
    case 6: return 'refunded';
    default: return 'unknown';
  }
};

export const getPaymentStatusLabel = (paymentStatusId: number): string => {
  switch (paymentStatusId) {
    case 1: return 'Pendente';
    case 2: return 'Pago';
    case 3: return 'Falhou';
    case 4: return 'Pago Offline';
    case 5: return 'Cancelado';
    case 6: return 'Reembolsado';
    default: return 'Desconhecido';
  }
};

export const getPaymentStatusColor = (paymentStatusId: number): string => {
  switch (paymentStatusId) {
    case 1: return 'yellow'; // pending
    case 2: return 'green';  // paid
    case 3: return 'red';    // failed
    case 4: return 'blue';   // paid_offline
    case 5: return 'gray';   // cancelled
    case 6: return 'purple'; // refunded
    default: return 'gray';
  }
};

export const isPaymentPaid = (paymentStatusId: number): boolean => {
  return paymentStatusId === 2 || paymentStatusId === 4; // PAID or PAID_OFFLINE
};
